import SelectDistrict from 'src/components/address/SelectDistrict';
import SelectProvince from 'src/components/address/SelectProvince';
import SelectSubDistrict from 'src/components/address/SelectSubDistrict';
import { Button, Col, Divider, Form, Input, Row, Checkbox, Result } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Typography from 'antd/es/typography';
import '../../styles/fix/input-dark.scss';
import { useCallback, useEffect, useState } from 'react';
import { MasterDataService } from '../../services/masterDataService';
import { MasterDataType } from '../..//constant/masterDataConstants';
import { HttpStatusCode } from 'axios';
import { MasterDataResponse } from '../../models/responses/MasterDataResponse';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { DateLength, RegexPattern } from '../../constant/constants';
import { useSessionContext } from '../../context/session-context';
import { AccountService } from 'src/services/accountService';
import { ArtistCreatingModel, ArtistProfileModel } from 'src/models/accountModel';
import { ArtistStatus } from 'src/constant/statusConstants';
import { HttpStatus } from 'src/constant/responseStatus';
import { ArtistService } from 'src/services/artistService';
import { RouteNames } from 'src/routes/routeName';
import { useNavigate } from 'react-router-dom';
import { RoleType } from 'src/constant/roleType';
import { PageDocument } from 'src/constant/stringConstants';
import saleforceService from 'src/services/saleforceService';
import ArtistTermOfService from '../artist/term-of-service';
import FullScreenMessageModal from 'src/components/modal/fullScreenModal';
import ArtistPrivacyPolicy from '../artist/privacy-policy';
import { CloseOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import TranslationKey from 'src/i18n/translation';
import { Loading } from 'src/components/loaging/Loading';
import MessageModal from 'src/components/modal/messageModal';
import { UserProfile } from 'src/models/responses/userProfile';

const { Title } = Typography;

export default function RegisterArtist() {
  const [postcode] = useState('');
  const [districtId, setDistrictId] = useState(0);
  const [provinceId, setProvinceId] = useState(0);
  const [subDistrictId, setSubDistrictId] = useState(0);
  const [styleOfWork, setStyleOfWork] = useState<MasterDataResponse[]>([]);
  const [platForm, setPlatForm] = useState<MasterDataResponse[]>([]);
  const [isShowOtherStyleOfWork, setIsShowOtherStyleOfWork] = useState(false);
  const [isShowOtherPlatform, setIsShowOtherPlatform] = useState(false);
  const [isShowMysitePlatform, setIsShowMysitePlatform] = useState(false);
  const [artist, setArtist] = useState<ArtistProfileModel>({} as ArtistProfileModel);
  const { userProfile } = useSessionContext();
  const [isAccept, setIsAccept] = useState(false);
  const [isOpenTerm, setIsOpenTerm] = useState(false);
  const [isOpenPolicy, setIsOpenPolicy] = useState(false);
  const [isCanReSubmitAgain, setIsCanReSubmitAgain] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [childModal, setChildModal] = useState<JSX.Element>(<></>);
  const [isWaitingForApprove, setIsWaitingForApprove] = useState(false);
  const [isRejectInPeriod, setIsRejectInPeriod] = useState(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${TranslationKey('registerArtist.title')} - ${PageDocument.Title}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });

    getStyleOfWorkAsync();
    getPlatformAsync();
  }, []);

  useEffect(() => {
    checkUserProfile(userProfile);
  }, [userProfile]);

  const checkUserProfile = useCallback(async (userProfileValue: UserProfile) => {
    if (!userProfileValue || !userProfileValue.userId) {
      return;
    }

    if (userProfileValue?.roles?.filter(r => r === RoleType.ARTIST).length > 0) {
      navigate(RouteNames.dashboard)
      return;
    }

    setIsBuyer(userProfileValue?.roles?.filter(r => r === RoleType.BUYER).length > 0);
    getExistArtistAsync(userProfileValue);
  }, []);

  const getExistArtistAsync = async (userProfileValue: UserProfile) => {
    if (!userProfileValue || !userProfileValue.userId) {
      return;
    }

    const { data, status } = await new AccountService().getArtistById(userProfileValue.userId);

    if (status !== HttpStatusCode.Ok) {
      return;
    }

    setArtist(data);

    if (data.artist.status === ArtistStatus.Approved) {
      navigate(RouteNames.dashboard);
      return;
    }

    if (data.artist.status === ArtistStatus.Pending) {
      setIsWaitingForApprove(true);
      return;
    }

    const updateArtistDate = new Date(data.artist.updatedInfo.at);
    const dataDiff = new DateLength().dayDiff(updateArtistDate, new Date());

    const artistIsRejectedInPeriod =
      data.artist.status === ArtistStatus.Rejected &&
      dataDiff <= DateLength.DAY_THREE_MONTH;

    setIsRejectInPeriod(artistIsRejectedInPeriod);
    setIsCanReSubmitAgain(!artistIsRejectedInPeriod);

    defaultAristFormValue(userProfileValue, data);
  }

  useEffect(() => {
    if (isBuyer && userProfile && userProfile.userId && !artist?.artist?.id) {
      form.setFieldValue('email', userProfile.email);
      form.setFieldValue('firstname', userProfile.firstname);
      form.setFieldValue('lastname', userProfile.lastname);
    }
  }, [isBuyer, userProfile]);

  const getStyleOfWorkAsync = useCallback(async () => {
    const { data, status } =
      await new MasterDataService().getGeneticAsync<MasterDataResponse[]>(
        MasterDataType.STYLE_OF_WORK
      );

    if (status !== HttpStatusCode.Ok) {
      return;
    }

    setStyleOfWork(data);
  }, []);

  const getPlatformAsync = useCallback(async () => {
    const { data, status } =
      await new MasterDataService().getGeneticAsync<MasterDataResponse[]>(
        MasterDataType.PLATFORM
      );

    if (status !== HttpStatusCode.Ok) {
      return;
    }

    setPlatForm(data);
  }, []);

  const validateCustomField = useCallback(
    (isRequired: boolean, key: string) => {
      if (isRequired) {
        form.setFields([
          {
            name: key,
            errors: [`${key} is required`],
          },
        ]);
      } else {
        form.setFields([
          {
            name: key,
            errors: undefined,
          },
        ]);
      }
    },
    [form]
  );

  const onFinish = (values: any) => {
    validateCustomField(!values.ProvinceId, 'ProvinceId');
    validateCustomField(!values.DistrictId, 'DistrictId');
    validateCustomField(!values.SubDistrictId, 'SubDistrictId');

    const validateAddress = !values.ProvinceId || !values.DistrictId || !values.SubDistrictId;
    const validateSocial = values.facebook || values.instagram || values.tiktok || values.other;

    if (!validateSocial || validateAddress) {
      return;
    }

    const artistCreating = {
      nameInfoEN_Firstname: values.firstname,
      nameInfoEN_Lastname: values.lastname,
      nameInfoEN_Nickname: values.nickname,
      nameInfoTH_Firstname: values.firstnameTh,
      nameInfoTH_Lastname: values.lastnameTh,
      nameInfoTH_Nickname: values.nicknameTh,
      email: values.email,
      phone: values.phone,
      password: isBuyer ? undefined : values.password,
      addressInfo_Address: values.address,
      addressInfo_Postcode: values.Postcode,
      addressInfo_ProvinceCode: values.ProvinceId,
      addressInfo_DistrictCode: values.DistrictId,
      addressInfo_SubDistrictCode: values.SubDistrictId,
      portfolio: values.portfolio,
      socialMediaInfo_Facebook: values.facebook,
      socialMediaInfo_Instagram: values.instagram,
      socialMediaInfo_Tiktok: values.tiktok,
      socialMediaInfo_Other: values.other,
      aboutYourSelf: values.aboutYourSelf,
      selfPromotion: values.selfPromotion,
      styleOfWork: JSON.stringify(values.styleOfWork),
      styleOfWork_Other: values.styleOfWorkOther,
      platform: JSON.stringify(values.platForm),
      platform_Mysite: values.platFormMySite,
      platform_Other: values.platFormOther,
      isAccept: true,
      Status: ArtistStatus.Pending,
    } as ArtistCreatingModel;

    if (isBuyer) {
      onAppendArtistRoleAsync(artistCreating);
      return;
    }

    onRegisterArtistAsync(artistCreating);
  };

  const onAppendArtistRoleAsync = async (artistCreatingModel: ArtistCreatingModel) => {
    setLoading(true);

    if (isCanReSubmitAgain) {
      const updateAristStatusResponse =
        await new ArtistService().UpdateArtistExistingData(
          artist.artist.id,
          artistCreatingModel);

      if (updateAristStatusResponse.status !== HttpStatus.OK) {
        showModalRegisterFail(
          TranslationKey('registerArtist.registerFail.title'),
          TranslationKey('registerArtist.registerFail.subTitle'),
          RouteNames.home,
          TranslationKey('goToHome'),
        );
        setLoading(false);
        return;
      }

      await saleforceService.postCreateArtistSaleforceAsync(artistCreatingModel);

      showModalRegisterSuccess(
        TranslationKey('registerArtist.registerSuccess.title'),
        TranslationKey('registerArtist.registerSuccess.subTitle'),
        RouteNames.home,
        TranslationKey('goToHome'),
      );
      setLoading(false);
      return;
    }

    const appendArtistRoleResponse =
      await new AccountService()
        .AppendArtistRole(
          artistCreatingModel,
          userProfile.userId);

    if (appendArtistRoleResponse.status !== HttpStatus.CREATED) {
      showModalRegisterFail(
        TranslationKey('registerArtist.registerFail.title'),
        TranslationKey('registerArtist.registerFail.subTitle'),
        RouteNames.home,
        TranslationKey('goToHome'),
      );
      setLoading(false);
      return;
    }

    await saleforceService.postCreateArtistSaleforceAsync(artistCreatingModel);

    showModalRegisterSuccess(
      TranslationKey('registerArtist.registerSuccess.title'),
      TranslationKey('registerArtist.registerSuccess.subTitle'),
      RouteNames.home,
      TranslationKey('goToHome'),
    );

    setLoading(false);
  };

  const onRegisterArtistAsync = async (artistCreatingModel: ArtistCreatingModel) => {
    setLoading(true);

    const registerArtistResponse =
      await new AccountService()
        .registerArtistAsync(artistCreatingModel);

    if (registerArtistResponse.status !== HttpStatus.CREATED) {
      showModalRegisterFail(
        TranslationKey('registerArtist.registerFail.title'),
        TranslationKey('registerArtist.registerFail.subTitle'),
        RouteNames.home,
        TranslationKey('goToHome'),
      );
      setLoading(false);
      return;
    }

    await saleforceService.postCreateArtistSaleforceAsync(artistCreatingModel);

    showModalRegisterSuccess(
      TranslationKey('registerArtist.registerSuccess.title'),
      TranslationKey('registerArtist.registerSuccess.subTitle') + ', ' + TranslationKey('register.popup.registerSuccessTitle'),
      RouteNames.login,
      TranslationKey('signIn'),
    );
    setLoading(false);
  };

  const showModalRegisterSuccess = (
    title: string,
    subTitle: string,
    nextRoute: string,
    nextRouteTextButtonKey: string,
  ) => {
    showModalMessage({
      status: 'success',
      title: title,
      subTitle: subTitle,
      extra: <Button size='large'
        type='primary'
        shape='round'
        className='btn-primary'
        onClick={() => {
          setIsOpenModal(false);
          navigate(nextRoute)
        }}>
        {TranslationKey(nextRouteTextButtonKey)}
      </Button>,
    });
  }

  const showModalRegisterFail = (
    title: string,
    subTitle: string,
    nextRoute: string,
    nextRouteTextButtonKey: string,
  ) => {
    showModalMessage({
      status: 'error',
      title: title,
      subTitle: subTitle,
      extra: <Button size='large'
        type='primary'
        shape='round'
        className='btn-primary'
        onClick={() => {
          setIsOpenModal(false);
          navigate(nextRoute)
        }}>
        {TranslationKey(nextRouteTextButtonKey)}
      </Button>,
    });
  }

  type ShowModalMessageProps = {
    status: 'success' | 'error' | 'info' | 'warning';
    title?: string;
    subTitle?: string;
    extra?: JSX.Element;
  };

  const showModalMessage = ({
    status,
    title,
    subTitle,
    extra,
  }: ShowModalMessageProps) => {
    setChildModal(
      <Result
        status={status}
        className='result-message'
        title={title}
        subTitle={subTitle}
        extra={extra}
      />
    );
    setIsOpenModal(true);
  };

  const onProvinceChange = (provinceId: number) => {
    setProvinceId(provinceId);
    form.setFieldValue('ProvinceId', provinceId);
    validateCustomField(!provinceId, 'ProvinceId');
  };

  const onDistrictChange = (districtId: number) => {
    setDistrictId(districtId);
    form.setFieldValue('DistrictId', districtId);
    validateCustomField(!districtId, 'DistrictId');
  };

  const onSubDistrictChange = (SubDistrictId: number) => {
    setSubDistrictId(SubDistrictId);
    form.setFieldValue('SubDistrictId', SubDistrictId);
    validateCustomField(!SubDistrictId, 'SubDistrictId');
  };

  const onStyleOfWorkChange = useCallback(
    (value: CheckboxChangeEvent) => {
      const targetId = value.target.value;
      const isChecked = value.target.checked;
      const object = styleOfWork.find((x) => x.value === targetId);

      if (object && object.name === 'Other') {
        setIsShowOtherStyleOfWork(isChecked);
      }
    },
    [styleOfWork]
  );

  const onPlatformChange = useCallback(
    (value: CheckboxChangeEvent) => {
      const targetId = value.target.value;
      const isChecked = value.target.checked;
      const object = platForm.find((x) => x.value === targetId);

      if (object && object.name === 'Other') {
        setIsShowOtherPlatform(isChecked);
      }

      if (object && object.name === 'My Site') {
        setIsShowMysitePlatform(isChecked);
      }
    },
    [platForm]
  );

  const defaultAristFormValue = (
    userProfileValue: UserProfile,
    artistProfileValue: ArtistProfileModel) => {
    form.setFieldValue('email', userProfileValue.email);
    form.setFieldValue('firstname', userProfileValue.firstname);
    form.setFieldValue('lastname', userProfileValue.lastname);

    var artist = artistProfileValue;

    if (!(artist && artist.artist)) {
      return;
    }

    form.setFieldValue('email', artist.artist.email);
    form.setFieldValue('phone', artist.artist.phone);
    form.setFieldValue('firstname', artist.artist.nameInfoEn.firstname);
    form.setFieldValue('lastname', artist.artist.nameInfoEn.lastname);
    form.setFieldValue('nickname', artist.artist.nameInfoEn.nickname);
    form.setFieldValue('firstnameTh', artist.artist.nameInfoTh.firstname);
    form.setFieldValue('lastnameTh', artist.artist.nameInfoTh.lastname);
    form.setFieldValue('nicknameTh', artist.artist.nameInfoTh.nickname);
    form.setFieldValue('address', artist.artist.addressInfo.address);
    form.setFieldValue('ProvinceId', artist.artist.addressInfo.provinceCode);
    form.setFieldValue('DistrictId', artist.artist.addressInfo.districtCode);
    form.setFieldValue('SubDistrictId', artist.artist.addressInfo.subDistrictCode);
    form.setFieldValue('Postcode', artist.artist.addressInfo.postcode);
    form.setFieldValue('portfolio', artist.artist.portfolio);
    form.setFieldValue('facebook', artist.artist.socialMediaInfo.facebook);
    form.setFieldValue('instagram', artist.artist.socialMediaInfo.instagram);
    form.setFieldValue('tiktok', artist.artist.socialMediaInfo.tiktok);
    form.setFieldValue('other', artist.artist.socialMediaInfo.other);
    form.setFieldValue('aboutYourSelf', artist.artist.aboutYourSelf);
    form.setFieldValue('selfPromotion', artist.artist.selfPromotion);
    form.setFieldValue('styleOfWork', JSON.parse(artist.artist.styleOfWork));
    form.setFieldValue('styleOfWorkOther', artist.artist.styleOfWork_Other);
    form.setFieldValue('platForm', JSON.parse(artist.artist.platform));
    form.setFieldValue('platFormMySite', artist.artist.platform_Mysite);
    form.setFieldValue('platFormOther', artist.artist.platform_Other);

    if (artist.artist.addressInfo.provinceCode) {
      setProvinceId(Number(artist.artist.addressInfo.provinceCode));
    }

    if (artist.artist.addressInfo.districtCode) {
      setDistrictId(Number(artist.artist.addressInfo.districtCode));
    }

    if (artist.artist.addressInfo.subDistrictCode) {
      setSubDistrictId(Number(artist.artist.addressInfo.subDistrictCode));
    }
  };

  const textTOS = () => {
    return (
      <>
        <Row className='d-flex justify-content-end'>
          <Col>
            <Button
              shape='round'
              type='text'
              className='text-primary'
              size='large'
              onClick={() => setIsOpenTerm(false)}
            >
              <CloseOutlined style={{ fontSize: '2em' }} />
            </Button>
          </Col>
        </Row>
        <ArtistTermOfService />
        <Row className='d-flex justify-content-center'>
          <Col span={16}>
            <Button
              shape='round'
              type='primary'
              className='btn btn-primary w-100'
              size='large'
              onClick={() => setIsOpenTerm(false)}
            >
              {TranslationKey('artist.iAcceptTheAboveTermsOfServices')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  const textPolicy = () => {
    return (
      <>
        <Row className='d-flex justify-content-end'>
          <Col>
            <Button
              shape='round'
              type='text'
              className='text-primary'
              size='large'
              onClick={() => setIsOpenPolicy(false)}
            >
              <CloseOutlined style={{ fontSize: '2em' }} />
            </Button>
          </Col>
        </Row>
        <ArtistPrivacyPolicy />
        <Row className='d-flex justify-content-center'>
          <Col span={16}>
            <Button
              shape='round'
              type='primary'
              className='btn btn-primary w-100'
              size='large'
              onClick={() => setIsOpenPolicy(false)}
            >
              {TranslationKey('artist.iAcceptTheAbovePrivacyAndPolicy')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }



  const buildRegisterAritstForm = () => {
    return <>
      <Row className='pt-5' justify='center'>
        <Col span={24}>
          <div className='bg-outline-gray mt-2 radius-32 px-5 py-5'>
            <Title className='text-primary text-center mb-0' level={1}>
              {TranslationKey('registerArtist.register')}
              <span className='text-primary-50' style={{ marginLeft: '0.5rem' }}>
                {TranslationKey('registerArtist.artist')}
              </span>
            </Title>
            <Form
              form={form}
              name='form_item_path'
              layout='vertical'
              className='form-input-contact'
              onFinish={onFinish}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name='email'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.email')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireEmail')
                    }]}
                  >
                    <Input
                      readOnly={isBuyer}
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.email')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='phone'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.phoneNumber')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requirePhoneNumber')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.phoneNumber')}
                      size='large' />
                  </Form.Item>
                </Col>
                {
                  isBuyer == false &&
                  <Col span={8}>
                    <Form.Item
                      name='password'
                      className='mt-4 text-light'
                      label={TranslationKey('password')}
                      rules={[
                        {
                          required: true,
                          message: TranslationKey('register.passwordInvalidRequired')
                        },
                        {
                          pattern: RegexPattern.password,
                          message: TranslationKey('register.passwordInvalidRule')
                        },
                      ]}
                    >
                      <Input.Password
                        name='password'
                        placeholder={TranslationKey('password')}
                        className='input-radius-dark w-100'
                        size='large'
                        iconRender={(visible) =>
                          visible
                            ? <EyeTwoTone style={{ color: '#FFF' }} />
                            : <EyeInvisibleOutlined style={{ color: '#FFF' }} />
                        }
                      />
                    </Form.Item>
                  </Col>
                }

              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name='firstname'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.firstNameEn')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireFirstNameEn')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.firstNameEn')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='lastname'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.lastNameEn')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireLastNameEn')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.lastNameEn')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='nickname'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.nickNameEn')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireNickNameEn')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.nickNameEn')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='firstnameTh'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.firstNameTh')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireFirstNameTh')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.firstNameTh')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='lastnameTh'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.lastNameTh')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireLastNameTh')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.lastNameTh')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name='nicknameTh'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.nickNameTh')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireNickNameTh')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.nickNameTh')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Divider className='text-primary'>
                  {TranslationKey('artist.address')}
                </Divider>
                <Col span={24}>
                  <Form.Item
                    name='address'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.addressDescription')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requireAddressDescription')
                    }]}
                  >
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.address')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name='ProvinceId'
                    label={TranslationKey('address.province')}
                    rules={[{
                      required: true,
                      message: TranslationKey('address.requireProvince')
                    }]}
                  >
                    <SelectProvince
                      className="input-radius-dark non-radius"
                      provinceID={provinceId}
                      sendProvinceID={onProvinceChange} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name='DistrictId'
                    label={TranslationKey('address.district')}
                    rules={[{
                      required: true,
                      message: TranslationKey('address.requireDistrict')
                    }]}
                  >
                    <SelectDistrict
                      className="input-radius-dark non-radius"
                      districtID={districtId}
                      provinceID={provinceId}
                      sendProvinceID={(id) => {
                        setProvinceId(id);
                      }}
                      sendDistrictID={onDistrictChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name='SubDistrictId'
                    label={TranslationKey('address.subDistrict')}
                    rules={[{
                      required: true,
                      message: TranslationKey('address.requireSubDistrict')
                    }]}
                  >
                    <SelectSubDistrict
                      className="input-radius-dark non-radius"
                      postCode={postcode}
                      districtID={districtId}
                      provinceID={provinceId}
                      subDistrictID={subDistrictId}
                      sendPostCode={(value) => {
                        form.setFieldValue('Postcode', value);
                      }}
                      sendSubDistrictID={onSubDistrictChange}
                      sendDistrictID={(id, subId) => {
                        setDistrictId(id);
                        setSubDistrictId(subId);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name='Postcode'
                    label={TranslationKey('address.postcode')}>
                    <Input
                      placeholder={TranslationKey('address.postcode')}
                      readOnly
                      className='input-radius-dark non-radius'
                      size='large' />
                  </Form.Item>
                </Col>
                <Divider className='text-primary'>
                  {TranslationKey('artist.artistDescription')}
                </Divider>
                <Col span={8}>
                  <Form.Item
                    name='portfolio'
                    className='mt-4 text-light'
                    label={`${TranslationKey('artist.portfolio')} (e.g. https://www.google.com)`}
                    rules={[
                      {
                        required: true,
                        message: TranslationKey('artist.requirePortfolio')
                      },
                    ]}>
                    <Input
                      className='input-radius-dark w-100'
                      placeholder={TranslationKey('artist.portfolio')}
                      size='large' />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    className='mt-4 text-light'
                    label={TranslationKey('artist.socialMedia.title')}
                    validateTrigger='onChange'
                    name='SocialGroup'
                    rules={[
                      {
                        validator: async () => {
                          const facebook = form.getFieldValue('facebook');
                          const instagram = form.getFieldValue('instagram');
                          const tiktok = form.getFieldValue('tiktok');
                          const other = form.getFieldValue('other');

                          if (!facebook && !instagram && !tiktok && !other) {
                            return Promise.reject(new Error(TranslationKey('artist.chooseAtLeaseOneAnswer')));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Form.Item
                      name='facebook'
                      style={{
                        display: 'inline-block',
                        marginBottom: '0px',
                        width: 'calc(25% - 8px)'
                      }}
                      rules={[{
                        required: false,
                      }]}
                    >
                      <Input
                        className='input-radius-dark'
                        placeholder={TranslationKey('artist.socialMedia.facebook')}
                        size='large'
                        onChange={() => form.validateFields(['SocialGroup'])}
                      />
                    </Form.Item>
                    <Form.Item
                      name='instagram'
                      style={{
                        display: 'inline-block',
                        marginBottom: '0px',
                        width: 'calc(25% - 8px)',
                        marginLeft: 8,
                      }}
                      rules={[{
                        required: false,
                      }]}
                    >
                      <Input
                        className='input-radius-dark'
                        placeholder={TranslationKey('artist.socialMedia.instagram')}
                        size='large'
                        onChange={() => form.validateFields(['SocialGroup'])}
                      />
                    </Form.Item>
                    <Form.Item
                      name='tiktok'
                      style={{
                        display: 'inline-block',
                        marginBottom: '0px',
                        width: 'calc(25% - 8px)',
                        marginLeft: 8,
                      }}
                      rules={[{
                        required: false,
                      }]}
                    >
                      <Input
                        className='input-radius-dark'
                        placeholder={TranslationKey('artist.socialMedia.tiktok')}
                        size='large'
                        onChange={() => form.validateFields(['SocialGroup'])}
                      />
                    </Form.Item>
                    <Form.Item
                      name='other'
                      style={{
                        display: 'inline-block',
                        marginBottom: '0px',
                        width: 'calc(25% - 8px)',
                        marginLeft: 8,
                      }}
                      rules={[{
                        required: false,
                      }]}
                    >
                      <Input
                        className='input-radius-dark'
                        placeholder={TranslationKey('artist.socialMedia.other')}
                        size='large'
                        onChange={() => form.validateFields(['SocialGroup'])}
                      />
                    </Form.Item>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name='aboutYourSelf'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.wouldYouLikeToShare')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requiredMessage')
                    }]}
                  >
                    <TextArea
                      className='input-radius-dark w-100'
                      rows={2}
                      style={{ borderRadius: '4px' }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name='selfPromotion'
                    className='mt-4 text-light'
                    label={TranslationKey('artist.definitions')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.requiredMessage')
                    }]}
                  >
                    <TextArea
                      className='input-radius-dark w-100'
                      rows={2} style={{ borderRadius: '4px' }} />
                  </Form.Item>
                </Col>
                <Divider className='text-primary'>
                  {TranslationKey('artist.styleOfWork')}
                </Divider>
                <Col span={12}>
                  <Form.Item
                    name='styleOfWork'
                    className='mt-4 text-light'
                    label={TranslationKey('registerArtist.youCanChoose')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.pleaseChooseMinimum')
                    }]}
                  >
                    <Checkbox.Group>
                      <Row>
                        {styleOfWork
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((item) => (
                            <Col key={item.sequence} span={24}>
                              <Checkbox
                                onChange={onStyleOfWorkChange}
                                className='text-white'
                                value={item.value}>
                                {item.name}
                              </Checkbox>
                            </Col>
                          ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item
                    name='styleOfWorkOther'
                    className={`text-light small-input ${isShowOtherStyleOfWork ? '' : 'hide'}`}
                  >
                    <Input className='input-radius-dark' placeholder='Other' size='large' />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='platForm'
                    className='mt-4 text-light'
                    label=
                    {TranslationKey('registerArtist.doYouSellYourWork')}
                    rules={[{
                      required: true,
                      message: TranslationKey('artist.pleaseChooseMinimum')
                    }]}
                  >
                    <Checkbox.Group>
                      <Row>
                        {platForm
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((item) => (
                            <Col key={item.sequence} span={24}>
                              <Checkbox
                                onChange={onPlatformChange}
                                className='text-white'
                                value={item.value}>
                                {item.name}
                              </Checkbox>
                            </Col>
                          ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item
                    name='platFormMySite'
                    className={`text-light small-input ${isShowMysitePlatform ? '' : 'hide'}`}
                  >
                    <Input className='input-radius-dark' placeholder='MySite' size='large' />
                  </Form.Item>
                  <Form.Item
                    name='platFormOther'
                    className={`text-light small-input ${isShowOtherPlatform ? '' : 'hide'}`}
                  >
                    <Input className='input-radius-dark' placeholder='Other' size='large' />
                  </Form.Item>
                </Col>
                <Col span={24} className='text-center'>
                  <Checkbox
                    value={isAccept}
                    className='my-3'
                    onChange={() => setIsAccept(!isAccept)}>
                    <span className='text-light'>
                      {TranslationKey('iAccept')}{' '}
                      <Button
                        type='text'
                        className='btn-link px-1'
                        onClick={() => setIsOpenTerm(true)}>
                        {TranslationKey('hTermOfService')}
                      </Button>{' '}
                      {TranslationKey('and')}{' '}
                      <Button
                        type='text'
                        className='btn-link px-1'
                        onClick={() => setIsOpenPolicy(true)}>
                        {TranslationKey('hPrivacyPolicy')}
                      </Button>
                    </span>
                  </Checkbox>
                </Col>
                <Col span={8} offset={8}>
                  <Button
                    shape='round'
                    type='primary'
                    className='btn btn-primary w-100'
                    htmlType='submit'
                    size='large'
                    disabled={!isAccept}
                  >
                    {TranslationKey('button.submit')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>
    </>
  }

  const buildInformWaitingForAdminApprove = () => {
    return <>
      <div className='container mt-6'>
        <Result
          status='info'
          title={TranslationKey('registerArtist.artistPending.title')}
          subTitle={TranslationKey('registerArtist.artistPending.subTitle')}
          className='result-order'
          extra={[
            <a key={'goToHome'}
              href={RouteNames.home}
              className='btn-orange-round'
              style={{ textDecoration: 'none' }}>
              {TranslationKey('goToHome')}
            </a>,
          ]}
        />
      </div>
    </>;
  }

  const buildRejectedInform = () => {
    return <>
      <div className='container mt-6'>
        <Result
          status='warning'
          title={TranslationKey('registerArtist.artistRejected.title')}
          subTitle={TranslationKey('registerArtist.artistRejected.subTitle')}
          className='result-order'
          extra={[
            <a key={'goToHome'}
              href={RouteNames.home}
              className='btn-orange-round'
              style={{ textDecoration: 'none' }}>
              {TranslationKey('goToHome')}
            </a>,
          ]}
        />
      </div>
    </>;
  }

  return <>
    {
      isWaitingForApprove
        ? buildInformWaitingForAdminApprove()
        : isRejectInPeriod
          ? buildRejectedInform()
          :
          <>
            <Loading isLoading={loading} />
            <div className='search pt-5'>
              <div className='container'>
                {buildRegisterAritstForm()}
              </div>
              <MessageModal
                isModalOpen={isOpenModal}
                closeOnBackdrop={false}
                onClose={() => setIsOpenModal(false)}
                children={childModal} />
              <FullScreenMessageModal
                isModalOpen={isOpenTerm}
                onClose={() => setIsOpenTerm(false)}
                children={textTOS()} />
              <FullScreenMessageModal
                isModalOpen={isOpenPolicy}
                onClose={() => setIsOpenPolicy(false)}
                children={textPolicy()} />
            </div>
          </>
    }
  </>
}
